/**
 * Per-alert minimise and close for the status banner (spec 02, section 3.5.3).
 *
 * The key includes the alert version (`updated_at`). A newer version misses
 * the old key, so the banner comes back. "View as" passes sessionStorage so
 * a staff preview does not change the staff member's own localStorage.
 */

export type BannerChrome = "full" | "min" | "closed";

const INDEX_KEY = "iq-ann:__index";
const KEEP = 20;

export function announcementStateKey(id: string, version: string): string {
  return `iq-ann:${id}:${version}`;
}

/** With two or more active alerts, only the first starts full. */
export function defaultBannerChrome(index: number, count: number): BannerChrome {
  return count >= 2 && index > 0 ? "min" : "full";
}

export function bannerStorage(viewAs: boolean): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    const store = viewAs ? window.sessionStorage : window.localStorage;
    const probe = "iq-ann:__probe";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

export function readBannerChrome(
  storage: Storage | null,
  id: string,
  version: string,
  index: number,
  count: number,
  canClose: boolean,
): BannerChrome {
  if (!storage) return defaultBannerChrome(index, count);
  let stored: string | null = null;
  try {
    stored = storage.getItem(announcementStateKey(id, version));
  } catch {
    return defaultBannerChrome(index, count);
  }
  if (stored === "min") return "min";
  if (stored === "closed") return canClose ? "closed" : "min";
  return defaultBannerChrome(index, count);
}

function readIndex(storage: Storage): string[] {
  try {
    const raw = storage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is string =>
        typeof item === "string" && item.startsWith("iq-ann:") && item !== INDEX_KEY,
    );
  } catch {
    return [];
  }
}

function writeIndex(storage: Storage, keys: string[]): void {
  const dropped = keys.length > KEEP ? keys.slice(0, keys.length - KEEP) : [];
  const next = keys.slice(-KEEP);
  for (const key of dropped) storage.removeItem(key);
  storage.setItem(INDEX_KEY, JSON.stringify(next));
}

export function rememberBannerChrome(
  storage: Storage | null,
  id: string,
  version: string,
  value: "min" | "closed",
): void {
  if (!storage) return;
  const key = announcementStateKey(id, version);
  try {
    storage.setItem(key, value);
    const index = readIndex(storage).filter((item) => item !== key);
    index.push(key);
    writeIndex(storage, index);
  } catch {
    // Private mode: React state still lasts for this page view.
  }
}

export function forgetBannerChrome(
  storage: Storage | null,
  id: string,
  version: string,
): void {
  if (!storage) return;
  const key = announcementStateKey(id, version);
  try {
    storage.removeItem(key);
    const index = readIndex(storage).filter((item) => item !== key);
    storage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {
    // Ignore a storage failure. The in-memory state already moved.
  }
}
